import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Row, Col, Select, Switch, Typography, message } from 'antd';
import { BellOutlined, GlobalOutlined, LockOutlined, SaveOutlined } from '@ant-design/icons';
import PageShell from '../components/Layout/PageShell';
import { authAPI } from '../services/api';

const { Text } = Typography;

const Settings: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);

  const loadSettings = async () => {
    setLoadingInitial(true);
    try {
      const response = await authAPI.getSettings();
      const settings = (response.data as any).data?.settings || {};
      form.setFieldsValue({
        notifications: {
          inApp: settings.notifications?.inApp ?? true,
          email: settings.notifications?.email ?? true,
          types: {
            lead: settings.notifications?.types?.lead ?? true,
            review: settings.notifications?.types?.review ?? true,
            system: settings.notifications?.types?.system ?? true,
          },
        },
        privacy: {
          allowMessages: settings.privacy?.allowMessages ?? true,
        },
        regional: {
          language: settings.regional?.language ?? 'en',
          currency: settings.regional?.currency ?? 'INR',
        },
      });
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoadingInitial(false);
    }
  };

  useEffect(() => {
    void loadSettings();
  }, []);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      await authAPI.updateSettings(values);
      message.success('Settings updated successfully');
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      title="Settings"
      description="Control how notifications work and how buyers can contact you."
    >
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="These settings actively control notification delivery and lead messaging behavior."
      />

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <Card className="h-full rounded-2xl border-slate-200 shadow-sm">
              <div className="mb-3 flex items-center gap-2">
                <BellOutlined className="text-[#0171BF]" />
                <Text strong>Notifications</Text>
              </div>
              <Text type="secondary" className="mb-3 block text-sm">
                Decide where and what notifications you receive.
              </Text>
              <Form.Item
                name={['notifications', 'inApp']}
                label="In-app notifications"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item
                name={['notifications', 'email']}
                label="Email notifications"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item
                name={['notifications', 'types', 'lead']}
                label="Lead notifications"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item
                name={['notifications', 'types', 'review']}
                label="Review notifications"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item
                name={['notifications', 'types', 'system']}
                label="System alerts"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card className="h-full rounded-2xl border-slate-200 shadow-sm">
              <div className="mb-3 flex items-center gap-2">
                <LockOutlined className="text-[#0171BF]" />
                <Text strong>Buyer messaging</Text>
              </div>
              <Text type="secondary" className="mb-3 block text-sm">
                Turn this off to stop new lead messages from buyers.
              </Text>
              <Form.Item
                name={['privacy', 'allowMessages']}
                label="Allow buyers to message me"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        <Row gutter={{ xs: 16, sm: 20, lg: 24 }} className="mt-4">
          <Col xs={24} lg={8}>
            <Card className="h-full rounded-2xl border-slate-200 shadow-sm">
              <div className="mb-3 flex items-center gap-2">
                <GlobalOutlined className="text-[#0171BF]" />
                <Text strong>Regional</Text>
              </div>
              <Form.Item name={['regional', 'language']} label="Language">
                <Select options={[{ value: 'en', label: 'English' }, { value: 'hi', label: 'Hindi' }]} />
              </Form.Item>
              <Form.Item name={['regional', 'currency']} label="Currency">
                <Select
                  options={[
                    { value: 'INR', label: 'INR (₹)' },
                    { value: 'USD', label: 'USD ($)' },
                    { value: 'EUR', label: 'EUR (€)' },
                    { value: 'GBP', label: 'GBP (£)' },
                  ]}
                />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        <div className="mt-4 flex justify-end">
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={loading || loadingInitial}
            disabled={loadingInitial}
            className="bg-[#0171BF]"
          >
            Save settings
          </Button>
        </div>
      </Form>
    </PageShell>
  );
};

export default Settings;
